import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Grid,
  Typography,
  TextField,
  Select,
  MenuItem,
  Divider,
  Dialog,
  DialogTitle,
  Box,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
} from '@mui/material';
import InvoicePaymentSeatDialog, { PaymentSeatData } from './InvoicePaymentSeatDialog';
//import InvoiceAccountingSeatWidget from './InvoiceAccountingSeatWidget';
import Autocomplete from '@mui/material/Autocomplete';
import { useNavigate, useParams } from 'react-router';
import intl from 'components/i18n/ReactIntlWrapper';
import NewInvoiceItemTable from '../../NewInvoice/NewInvoiceItemTable';
import NewInvoiceSummary from '../../NewInvoice/NewInvoiceSummary';
import { DemoContainer } from '@mui/x-date-pickers/internals/demo';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { useInvoice } from '../../common/InvoiceProvider';
import { useApi } from 'api/ApiProvider';
import Api from 'axiosApi/api';
import { useToastMessageQueue } from 'components/ToastMessages/ToastMessageProvider';
import WorkflowActionsBar from '../../Workflow/WorkflowActionsBar';
import Spinner from '../../Spinner';
import { useWorkflow } from 'components/Workflow/WorkflowContext';
import { handleAPIError } from '../../../common/errorHandler';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import ErrorSummary from '../../common/ErrorSummary';

const AccountsPayableForm = ({ onlyView = false }) => {
  const {
    currencies,
    invoiceTypes,
    input,
    setInput,
    isManualCreation,
    errors,
    setErrors,
  } = useInvoice();
  const [statusLabel, setStatusLabel] = useState<{ label: string; color: string }>();
  const [loading, setLoading] = useState<boolean>(false);
  const [workflowLabelKeys, setWorkflowLabelKeys] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [paymentSeatOpen, setPaymentSeatOpen] = useState(false);
  const pendingPaymentResolve = useRef<((result: boolean | 'handled') => void) | null>(null);
  const [toOptions, setToOptions] = useState<{ id: number; name: string }[]>([]);
  const [toLoading, setToLoading] = useState(false);
  const [toInputValue, setToInputValue] = useState('');
  const [toValue, setToValue] = useState<{ id: number; name: string } | null>(null);
  const pendingSubmitArgs = React.useRef<{ resolve?: (value: boolean) => void } | null>(null);
  const toast = useToastMessageQueue();
  const api: Api = useApi();
  const navigate = useNavigate();
  const { id: jobId } = useParams<{ id: string }>();
  const { workflow } = useWorkflow();

  const searchTo = useCallback(async (term: string) => {
    setToLoading(true);
    try {
      const res = await api.invoiceApi.apiVversionPersonaEligibleInvoiceToTypeAheadGet('1', term);
      const results = (res.data?.data?.queryResult ?? [])
        .map((p) => ({ id: p.id, name: p.name }))
        .sort((a, b) => a.name.localeCompare(b.name));
      setToOptions(results);
    } finally {
      setToLoading(false);
    }
  }, [api]);

  // Pre-populate options with the current persona so the Autocomplete shows it on load
  useEffect(() => {
    if (!input.idTo) return;
    if (toValue) return;
    const seed = (p: { id: number; name: string }) => {
      setToOptions([p]);
      setToValue(p);
      setToInputValue(p.name);
    };
    if (input.to) {
      seed({ id: input.idTo, name: input.to });
    } else {
      api.invoiceApi.apiVversionPersonaIdGet(input.idTo, '1').then((res) => {
        const p = res.data?.data;
        if (p?.id && p?.name) seed({ id: p.id, name: p.name });
      });
    }
  }, [input.idTo, input.to]);

  useEffect(() => {
    const newItem = {
      id: 1,
      amount: '',
      unit: 1,
      invoiceItemTaxes: [],
    };
    input.invoiceItems = [newItem];
  }, []);

  useEffect(() => {
    setWorkflowLabelKeys(
      workflow?.states?.map((state) => ({ label: state.labelKey, id: state.id }))
    );
  }, [workflow]);

  useEffect(() => {
    const currentStatus = workflowLabelKeys?.filter(
      (labelKey) => labelKey.id === input.idWorkflowState
    );
    if (!currentStatus?.[0]) {
      setStatusLabel({ label: 'invoicing.manualReview', color: 'primary' });
    } else {
      const label = currentStatus[0]?.label;
      const color =
        label === 'Pending' || label === 'Pendiente'
          ? 'warning'
          : label === 'Approved' || label === 'Aprobado'
            ? 'success'
            : label === 'Observed' || label === 'Observado'
              ? 'default'
              : label === 'Open' || label === 'Abierto'
                ? 'primary'
                : label === 'Reopen' || label === 'Reabrir'
                  ? 'secondary'
                  : 'error';
      setStatusLabel({ label, color });
    }
  }, [workflowLabelKeys, input?.idWorkflowState]);

  useEffect(() => {
    const handleAutofill = () => {
      const inputs = document.querySelectorAll('input, select');
      inputs.forEach((input) => {
        const element = input as HTMLInputElement | HTMLSelectElement;
        if (element.value && !element.dataset.initialized) {
          element.dataset.initialized = 'true';
          const event = new Event('change', { bubbles: true });
          element.dispatchEvent(event);
        }
      });
    };

    handleAutofill();
    const timeoutId = setTimeout(handleAutofill, 100);

    return () => clearTimeout(timeoutId);
  }, []);

  // Mark a field as reviewed (clears confidence flags) without changing its value
  const handleFieldReview = (name: string) => {
    setErrors((prevErrors) => {
      if (prevErrors?.[name] === 'missing confidence' || prevErrors?.[name] === 'low confidence') {
        return { ...prevErrors, [name]: '' };
      }
      return prevErrors;
    });
  };

  const handleInputChange = (name, value, extra?: Record<string, unknown>) => {
    setInput({
      ...input,
      [name]: value,
      ...(extra ?? {}),
    });
    //validate errors
    setErrors((prevErrors) => ({
      ...prevErrors,
      [name]: '',
    }));
  };

  // Called before WorkflowActionsBar opens its comment modal.
  // Returns false to abort the action (and show the unreviewed-fields dialog).
  const handleBeforeAction = (targetStateId: number): Promise<boolean | 'handled'> => {
    // Check for unreviewed confidence fields first
    const unreviewedFields = Object.entries(errors || {}).filter(
      ([, value]) => value === 'missing confidence' || value === 'low confidence'
    );
    if (unreviewedFields.length > 0) {
      setConfirmOpen(true);
      return new Promise<boolean | 'handled'>((resolve) => {
        pendingSubmitArgs.current = { resolve };
      });
    }

    // Intercept the "Paid" transition (workflowStateTo = 118)
    if (targetStateId === 118) {
      setPaymentSeatOpen(true);
      return new Promise<boolean | 'handled'>((resolve) => {
        pendingPaymentResolve.current = resolve;
      });
    }

    return Promise.resolve(true);
  };

  const handlePaymentSeatConfirm = async (data: PaymentSeatData) => {
    try {
      await api.invoiceApiWrapper.payInvoice('1', input.id, {
        amount: Math.round(data.amount * 100),
        incoming: 0, // outgoing / payment
        date: data.date,
        idAccount: data.idAccount,
        description: data.description,
      });
      setPaymentSeatOpen(false);
      if (pendingPaymentResolve.current) {
        pendingPaymentResolve.current('handled');
        pendingPaymentResolve.current = null;
      }
      navigate('/accountspayable/all');
    } catch (error) {
      handleAPIError(error, toast, {});
      if (pendingPaymentResolve.current) {
        pendingPaymentResolve.current(false);
        pendingPaymentResolve.current = null;
      }
    }
  };

  const handlePaymentSeatConfirmExisting = async (accountingSeatId: number) => {
    try {
      await api.invoiceApiWrapper.linkExistingLedgerEntry('1', input.id, accountingSeatId);
      setPaymentSeatOpen(false);
      if (pendingPaymentResolve.current) {
        pendingPaymentResolve.current('handled');
        pendingPaymentResolve.current = null;
      }
      navigate('/accountspayable/all');
    } catch (error) {
      handleAPIError(error, toast, {});
      if (pendingPaymentResolve.current) {
        pendingPaymentResolve.current(false);
        pendingPaymentResolve.current = null;
      }
    }
  };

  const handlePaymentSeatMarkPaidOnly = async () => {
    try {
      await api.invoiceApiWrapper.markInvoiceAsPaid('1', input.id);
      setPaymentSeatOpen(false);
      if (pendingPaymentResolve.current) {
        pendingPaymentResolve.current('handled');
        pendingPaymentResolve.current = null;
      }
      navigate('/accountspayable/all');
    } catch (error) {
      handleAPIError(error, toast, {});
      if (pendingPaymentResolve.current) {
        pendingPaymentResolve.current(false);
        pendingPaymentResolve.current = null;
      }
    }
  };

  const handlePaymentSeatCancel = () => {
    setPaymentSeatOpen(false);
    if (pendingPaymentResolve.current) {
      pendingPaymentResolve.current(false);
      pendingPaymentResolve.current = null;
    }
  };

  const handleSave = async (id, comments) => {
    setLoading(true);
    // Module 5: DynamoDB invoice — no MySQL entity, call jobId endpoint with comments then navigate
    //THIS IS VERY VERY WRONG
    /*if (workflow?.idModule === 5) {
      try {
        await api.invoiceApiWrapper.updateJobInvoiceWorkflowState('1', jobId, id, comments);
        navigate('/accountspayable');
      } catch (error) {
        handleAPIError(error, toast, {});
      } finally {
        setLoading(false);
      }
      return;
    }*/
    var result = false;
    try {
      input.comments = comments;
      const errorList = {};
      if (!input.number) errorList['number'] = `${intl.get('newInvoice.numberRequired')}`;
      if (!input.idCurrency || input.idCurrency === 0 || input.idCurrency === '')
        errorList['idCurrency'] = `${intl.get('newInvoice.currencyRequired')}`;
      if (!input.idInvoiceType || input.idInvoiceType === 0 || input.idInvoiceType === '')
        errorList['idInvoiceType'] = `${intl.get('newInvoice.isRequired')}`;
      if (!input.date) errorList['date'] = `${intl.get('newInvoice.dateRequired')}`;
      if (!input.addressFrom) errorList['addressFrom'] = `${intl.get('newInvoice.isRequired')}`;
      if (!input.addressTo) errorList['addressTo'] = `${intl.get('newInvoice.isRequired')}`;
      if (!input.idFrom || input.idFrom === 0 || input.idFrom === '')
        errorList['idFrom'] = `${intl.get('newInvoice.profileRequired')}`;
      if (!input.idTo || input.idTo === 0 || input.idTo === '')
        errorList['idTo'] = `${intl.get('newInvoice.isRequired')}`;
      if (!input.invoiceItems || input.invoiceItems.length === 0)
        errorList['invoiceItems'] = `${intl.get('newInvoice.itemsRequired')}`;
      input.invoiceItems.forEach((item) => {
        if (
          item.description === '' ||
          !item.description ||
          item.quantity === '' ||
          !item.quantity
        ) {
          errorList['invoiceItems'] = 'Missing one or more field in items';
        }
      });
      setErrors((prevErrors) => ({ ...prevErrors, ...errorList }));

      console.log('[AccountsPayable] handleSave validation errorList:', errorList);
      console.log('[AccountsPayable] input snapshot:', {
        number: input.number,
        idCurrency: input.idCurrency,
        idInvoiceType: input.idInvoiceType,
        date: input.date,
        addressFrom: input.addressFrom,
        addressTo: input.addressTo,
        idFrom: input.idFrom,
        idTo: input.idTo,
        invoiceItems: input.invoiceItems?.length,
        attachments: input.attachments,
        isManualCreation,
        items: input.invoiceItems?.map(i => ({ desc: i.description, qty: i.quantity, rate: i.rate })),
      });

      // Block if there are hard validation errors
      if (Object.keys(errorList).length > 0) {
        setLoading(false);
        return false;
      }

      result = await executeSubmit(id, comments);
    } catch (error) {
      handleAPIError(error, toast, errors);
      setErrors({ ...errors });
      setLoading(false);
      result = false;
      throw error;
    }
    setLoading(false);
    return result;
  };

  const executeSubmit = async (id, comments) => {
    setLoading(true);
    console.log('[AccountsPayable] executeSubmit called with idWorkflowState:', id);
    try {
      const inputCopy = { ...input };
      inputCopy.comments = comments;

      if (inputCopy.invoiceItems) {
        console.log('[AP] raw invoiceItems before remap:', JSON.stringify(inputCopy.invoiceItems.map(i => ({
          description: i.description,
          quantity: i.quantity, quantityType: typeof i.quantity,
          rate: i.rate, rateType: typeof i.rate,
          amount: i.amount, amountType: typeof i.amount,
        })), null, 2));
        inputCopy.invoiceItems = inputCopy.invoiceItems.map((item) => {
          const newItem = { ...item };
          const rate = Number(newItem.rate) || 0;
          // The backend's InvoiceUtils.CalculateInvoiceTotals computes Amount × Quantity itself,
          // so we must send the unit price (rate), NOT the line total (qty × rate).
          // Fall back to the raw amount field only when rate is absent (e.g. OCR items).
          newItem.amount = rate !== 0 ? rate : Number(newItem.amount) || 0;

          // Strip out empty/placeholder tax entries before submitting
          const validTaxes = (newItem.invoiceItemTaxes ?? []).filter(
            (tax) => tax.id !== '' && tax.id !== 0 && tax.id !== null
          );

          if (validTaxes.length > 0) {
            const itemAmount = newItem.amount;
            newItem.invoiceItemTaxes = validTaxes.map((tax) => ({
              idTaxRate: tax.id,
              taxAmount:
                tax.amount === 0 ? itemAmount * (Number(tax.rate) / 100) : Number(tax.amount),
              taxRate: Number(tax.rate) || 0,
              invoiceItemId: 0,
              invoiceItem: {},
            }));
          } else {
            newItem.invoiceItemTaxes = [];
          }
          return newItem;
        });
      }

      inputCopy.idWorkflowState = id;
      inputCopy.number = inputCopy.number.toString();
      inputCopy.idInvoiceType = Number(inputCopy.idInvoiceType);

      // Strip navigation properties from top-level invoice taxes.
      // Exclude taxes with no real idTaxRate (0 originals were remapped to 10000+ as display placeholders).
      if (inputCopy.invoiceTaxes) {
        inputCopy.invoiceTaxes = inputCopy.invoiceTaxes
          .filter((tax) => tax.idTaxRate && tax.idTaxRate > 0 && tax.idTaxRate < 10000)
          .map((tax) => ({
            idTaxRate: tax.idTaxRate,
            taxAmount: Number(tax.taxAmount) || 0,
            taxRate: Number(tax.taxRate) || 0,
            description: tax.description ?? tax.taxRateEntity?.description ?? '',
          }));
      }

      if (inputCopy.attachments && !inputCopy.fileURI) {
        inputCopy.fileURI = inputCopy.attachments[0].fileURI;
      }

      if (inputCopy.invoice) {
        const reader = new FileReader();
        reader.readAsDataURL(inputCopy.invoice);
        await new Promise<void>((resolve, reject) => {
          reader.onload = () => {
            inputCopy.fileURI = reader.result;
            delete inputCopy.invoice;
            resolve();
          };
          reader.onerror = () => {
            reject(new Error('Failed to read file'));
          };
        });
      }

      const formatedInput = {
        ...inputCopy,
        date: new Date(inputCopy.date).toISOString().split('T')[0],
      };
      const response = await api.invoiceApi.apiVversionInvoiceAccountPayablePost('1', formatedInput);
      if (response.data?.data) {
        toast.success({ message: intl.get('newInvoice.toast.success.handleSaveAsDraft') });
        navigate('/accountspayable/all');
        return true;
      }
    } catch (error) {
      const errorsSnapshot = { ...errors };
      handleAPIError(error, toast, errorsSnapshot);
      setErrors(errorsSnapshot);
      if (!error.response) {
        toast.error({ header: intl.get('error'), body: error?.message });
      }
    } finally {
      setLoading(false);
    }
    return false;
  };

  const isDisabled = () => {
    return (
      !input.number ||
      !input.idCurrency ||
      !input.idInvoiceType ||
      !input.date ||
      !input.addressFrom ||
      !input.addressTo ||
      !input.idFrom ||
      !input.idTo ||
      !input.invoiceItems ||
      input.invoiceItems?.some(
        (item) =>
          !item.description ||
          item.description === '' ||
          !item.quantity ||
          item.quantity === ''
      )
    );
  };

  return (
    <>
      {loading && (
        <Box sx={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: 'rgba(255,255,255,0.5)' }}>
          <Spinner />
        </Box>
      )}
      <ErrorSummary errors={errors} showEverything={true} />
      <Grid container spacing={2}>
        <Grid container spacing={2} sx={{ width: '100%' }}>
          <Grid
            sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
          >
            <Typography gutterBottom>{intl.get('invoicesList.invoice.number')}</Typography>
            {errors?.number && errors?.number !== 'missing confidence' && (
              <Typography className="text-danger">{errors?.number}</Typography>
            )}
            <TextField
              fullWidth
              size="small"
              id="number"
              name="number"
              placeholder={intl.get('newInvoice.invoiceNumber')}
              value={input?.number}
              onChange={(e) => handleInputChange('number', e.target.value)}
              onBlur={(e) => { handleInputChange('number', e.target.value); handleFieldReview('number'); }}
              disabled={onlyView}
              error={!!errors?.number}
            />
          </Grid>
          <Grid
            sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
          >
            <Typography gutterBottom>{intl.get('newInvoice.invoiceCurrency')}</Typography>
            {errors?.idCurrency && errors?.idCurrency !== 'missing confidence' && (
              <Typography className="text-danger">{errors?.idCurrency}</Typography>
            )}
            <Select
              fullWidth
              size="small"
              id="currency"
              name="currency"
              value={input?.idCurrency}
              displayEmpty
              disabled={onlyView}
              inputProps={{ placeholder: intl.get('newInvoice.invoiceCurrency') }}
              onChange={(e) => handleInputChange('idCurrency', e.target.value)}
              onBlur={(e) => { handleInputChange('idCurrency', e.target.value); handleFieldReview('idCurrency'); handleFieldReview('currency'); }}
              error={!!errors?.idCurrency}
            >
              <MenuItem disabled value="">
                <em>{intl.get('newInvoice.invoiceCurrency')}</em>
              </MenuItem>
              {currencies?.map((currency) => (
                <MenuItem key={currency.id} value={currency.id}>
                  {currency.code}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ width: '100%' }}>
          <Grid
            sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
          >
            <Typography gutterBottom className={`${errors?.addressFrom ? 'text-danger' : ''}`}>
              {intl.get('newInvoice.invoiceAddressFrom')}
            </Typography>
            <TextField
              fullWidth
              size="small"
              id="addressFrom"
              name="addressFrom"
              placeholder={intl.get('newInvoice.invoiceAddressFrom')}
              value={input?.addressFrom}
              onChange={(e) => handleInputChange('addressFrom', e.target.value)}
              onBlur={(e) => { handleInputChange('addressFrom', e.target.value); handleFieldReview('addressFrom'); }}
              disabled={onlyView}
              error={!!errors?.addressFrom}
            />
          </Grid>
          <Grid
            sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
          >
            <Typography gutterBottom className={`${errors?.date ? 'text-danger' : ''}`}>
              {intl.get('newInvoice.date')}
            </Typography>
            <DemoContainer components={['DatePicker']}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  slotProps={{
                    openPickerIcon: { fontSize: 'small' },
                    textField: {
                      size: 'small',
                      error: !!errors?.date,
                    },
                  }}
                  disabled={onlyView}
                  value={new Date(input?.date)}
                  onChange={(e) => handleInputChange('date', e)}
                  onClose={() => handleFieldReview('date')}
                  sx={{
                    '& .MuiInputBase-root': {
                      overflow: 'hidden',
                      '&.Mui-error': {
                        border: '1px solid red !important',
                      },
                    },
                  }}
                />
              </LocalizationProvider>
            </DemoContainer>
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ width: '100%' }}>
          <Grid
            sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
          >
            <Autocomplete
              options={toOptions}
              getOptionLabel={(o) => o.name ?? ''}
              isOptionEqualToValue={(o, v) => o.id === v.id}
              loading={toLoading}
              disabled={onlyView}
              value={toValue}
              inputValue={toInputValue}
              onInputChange={(_e, value, reason) => {
                if (reason === 'input') {
                  setToInputValue(value);
                  searchTo(value);
                } else if (reason === 'clear') {
                  setToInputValue('');
                  setToValue(null);
                }
              }}
              onChange={(_e, persona) => {
                if (!persona) return;
                setToValue(persona);
                setToInputValue(persona.name);
                handleInputChange('idTo', persona.id, { to: persona.name });
                handleFieldReview('idTo');
                handleFieldReview('personaTo');
                handleFieldReview('to');
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size="small"
                  label={intl.get('newInvoice.to')}
                  error={!!errors?.idTo && errors?.idTo !== 'missing confidence'}
                  helperText={errors?.idTo && errors?.idTo !== 'missing confidence' ? errors.idTo : undefined}
                />
              )}
            />
          </Grid>
          <Grid
            sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}
          >
            <Typography gutterBottom className={`${errors?.addressTo ? 'text-danger' : ''}`}>
              {intl.get('newInvoice.invoiceAddressTo')}
            </Typography>
            <TextField
              fullWidth
              size="small"
              id="addressTo"
              name="addressTo"
              placeholder={intl.get('newInvoice.invoiceAddressTo')}
              value={input?.addressTo}
              onChange={(e) => handleInputChange('addressTo', e.target.value)}
              onBlur={(e) => { handleInputChange('addressTo', e.target.value); handleFieldReview('addressTo'); }}
              disabled={onlyView}
              error={!!errors?.addressTo}
            />
          </Grid>
        </Grid>
      </Grid>
      <Divider sx={{ my: 3 }} />
      <Grid
        container
        spacing={2}
        size={{
          xl: 12,
          md: 6,
        }}
      >
        <Grid
          container
          className="justify-content-start"
          size={{
            xl: 6,
          }}
        >
          <Typography
            className={`${errors?.idInvoiceType ? 'text-danger' : ''}`}
            style={{ marginTop: '5px' }}
          >
            {intl.get('newInvoice.invoiceType')}{' '}
            {errors?.idInvoiceType && errors?.idInvoiceType !== 'missing confidence' && (
              <span className="text-danger">{errors?.idInvoiceType}</span>
            )}
          </Typography>
          <Grid
            size={{
              xl: 12,
            }}
          >
            <Select
              id="invoiceType"
              name="invoiceType"
              value={input?.idInvoiceType === 0 ? '' : input?.idInvoiceType}
              fullWidth
              displayEmpty
              disabled={onlyView}
              inputProps={{
                placeholder: intl.get('newInvoice.invoiceType'),
              }}
              onChange={(e) => handleInputChange('idInvoiceType', e.target.value)}
              onBlur={(e) => { handleInputChange('idInvoiceType', e.target.value); handleFieldReview('idInvoiceType'); }}
              error={errors?.idInvoiceType ? true : false}
            >
              <MenuItem disabled value="">
                <em>{intl.get('newInvoice.invoiceType')}</em>
              </MenuItem>
              {invoiceTypes?.map((invoiceType) => (
                <MenuItem key={invoiceType.id} value={invoiceType.id}>
                  {invoiceType.name}
                </MenuItem>
              ))}
            </Select>
          </Grid>
        </Grid>
        <Grid
          container
          className="justify-content-end"
          size={{
            xl: 6,
          }}
        >
          <Grid
            container
            className="justify-content-start"
            size={{
              xl: 10,
            }}
          >
            <Typography
              className={`${errors?.paymentTerms ? 'text-danger' : ''}`}
              style={{ marginTop: '5px' }}
            >
              {intl.get('newInvoice.paymentTerms')}{' '}
              {errors?.paymentTerms && errors?.paymentTerms !== 'missing confidence' && (
                <span className="text-danger">{errors?.paymentTerms}</span>
              )}
            </Typography>
          </Grid>
          <Grid
            container
            className="justify-content-end"
            size={{
              xl: 10,
            }}
          >
            <TextField
              id="paymentTerms"
              name="paymentTerms"
              placeholder={intl.get('newInvoice.paymentTerms')}
              value={input?.paymentTerms}
              onChange={(e) => handleInputChange('paymentTerms', e.target.value)}
              onBlur={(e) => { handleInputChange('paymentTerms', e.target.value); handleFieldReview('paymentTerms'); }}
              disabled={onlyView}
              fullWidth
              error={errors?.paymentTerms ? true : false}
            />
          </Grid>
        </Grid>
      </Grid>
      {errors?.invoiceItems && (
        <Grid
          container
          className="justify-content-start"
          style={{ marginTop: '10px', marginBottom: '-10px' }}
          size={{
            xl: 12,
          }}
        >
          <Typography className="text-danger">{errors?.invoiceItems}</Typography>
        </Grid>
      )}
      <Grid sx={{ marginTop: '20px', width: '100%', xl: 12 }}>
        <NewInvoiceItemTable disabled={onlyView} />
      </Grid>
      <Grid
        container
        style={{ marginTop: '10px' }}
        size={{
          xl: 12,
        }}
      >
        <Grid
          size={{
            xl: 6,
          }}
        ></Grid>
        <Grid sx={{ marginTop: '20px', width: '100%' }} size={{ xl: 6 }}>
          <NewInvoiceSummary disabled={onlyView} />
        </Grid>
      </Grid>
      <Grid className="justify-content-end">
        <Grid className="d-flex justify-content-end" sx={{ marginTop: '20px' }}>
          <WorkflowActionsBar
            viewMode=""
            onActionClick={handleSave}
            onBeforeAction={handleBeforeAction}
            id={workflow?.idModule === 5 ? null : input.id}
            stateId={input.idWorkflowState}
            loading={loading}
            disabled={isDisabled() || onlyView}
            returnPath={workflow?.idModule === 4 ? '/accountspayable/all' : workflow?.idModule === 5 ? '/accountspayable' : undefined}
          />
        </Grid>
      </Grid>

      <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
        <DialogTitle>{intl.get('accountsPayable.validate.confirmSubmit.title')}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {intl.get('accountsPayable.validate.confirmSubmit.body')}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => {
            setConfirmOpen(false);
            if (pendingSubmitArgs.current?.resolve) {
              pendingSubmitArgs.current.resolve(false);
              pendingSubmitArgs.current = null;
            }
          }}>
            {intl.get('accountsPayable.validate.confirmSubmit.cancel')}
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              setConfirmOpen(false);
              if (pendingSubmitArgs.current?.resolve) {
                pendingSubmitArgs.current.resolve(true);
                pendingSubmitArgs.current = null;
              }
            }}
          >
            {intl.get('accountsPayable.validate.confirmSubmit.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      <InvoicePaymentSeatDialog
        open={paymentSeatOpen}
        invoiceId={input?.id ?? 0}
        invoiceNumber={input?.number ?? ''}
        invoiceFrom={input?.from ?? ''}
        invoiceAmount={input?.amount ?? 0}
        invoiceCurrencyCode={
          currencies?.find((c) => c.id === input?.idCurrency)?.code ?? ''
        }
        onConfirm={handlePaymentSeatConfirm}
        onConfirmExisting={handlePaymentSeatConfirmExisting}
        onMarkPaidOnly={handlePaymentSeatMarkPaidOnly}
        onCancel={handlePaymentSeatCancel}
      />
    </>
  );
};

export default AccountsPayableForm;
